import { useState, useEffect, useMemo } from 'react';
import {
    useMediaQuery,
    Tabs,
    Tab,
    Avatar,
    Badge,
    Divider,
    IconButton,
    Tooltip,
    Skeleton,
    InputBase,
    Paper
} from '@mui/material';
import {
    IoPersonCircleOutline,
    IoShieldCheckmarkOutline,
    IoOptionsOutline,
    IoTimeOutline,
    IoKeyOutline,
    IoTrashOutline,
    IoRefreshOutline
} from 'react-icons/io5';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import ProfilePage from '@UserManagementPages/ProfilePage';
import ChangePasswordPage from '@UserManagementPages/ChangePasswordPage';
import DeleteAccountPage from '@UserManagementPages/DeleteAccountPage';
import AccessSettingsPage from '@UserManagementPages/AccessSettingsPage';
import PreferencesPage from '@UserManagementPages/PreferencesPage';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import ErrorHandler from '@Root/ErrorHandler';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './SettingsPage.scss';

export default function SettingsPage() {

    const [loadingUser, setLoadingUser] = useState(false);
    const [settingTabView, setSettingTabView] = useState(0);
    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();
    const isNarrow = useMediaQuery('(max-width: 1280px)');
    const isMobile = useMediaQuery('(max-width: 640px)');
    const { activeUserId, activeUser } = useReduxActiveUser();
    const className = 'settingsPage';

    const toggleSettingTabView = (_e, view) => {
        setSettingTabView(view);
    };

    const userName = activeUser?.fullName || activeUser?.userName || 'User';
    const userEmail = activeUser?.email || '';

    const initials = useMemo(() => {
        if (!userName) return 'U';
        const parts = userName.trim().split(/\s+/);
        const first = parts[0]?.[0] || '';
        const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
        return (first + (last || '')).toUpperCase();
    }, [userName]);

    const refreshUser = async () => {
        SnackbarManager.success('Profile refreshed');
    };

    return (
        <div className={className}>
                {/* Sticky Header */}
                <div className={className + '__header'}>
                    <div className={className + '__header__left'}>
                        <h1 className={className + '__title'}>Settings</h1>
                    </div>
                    <div className={className + '__header__right'}>
                        <Tooltip title="Refresh profile">
                            <IconButton onClick={refreshUser} size="small">
                                <IoRefreshOutline />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                {/* Layout */}
                <div className={className + '__content'}>
                    {/* Sidebar */}
                    <aside className={className + '__sidebar'}>
                        <div className={className + '__profileCard'}>
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={<span className={className + '__statusDot'} />}
                            >
                                {loadingUser ? (
                                    <Skeleton variant="circular" width={64} height={64} />
                                ) : activeUser?.photo ? (
                                    <Avatar src={activeUser.photo} sx={{ width: 64, height: 64 }} />
                                ) : (
                                    <Avatar sx={{ width: 64, height: 64, bgcolor: '#2784B9', fontWeight: 700 }}>
                                        {initials}
                                    </Avatar>
                                )}
                            </Badge>
                            <div className={className + '__profileCard__info'}>
                                {loadingUser ? (
                                    <>
                                        <Skeleton width={120} height={20} />
                                        <Skeleton width={150} height={16} />
                                    </>
                                ) : (
                                    <>
                                        <div className={className + '__profileCard__name'}>{userName}</div>
                                        {!!userEmail && <div className={className + '__profileCard__email'}>{userEmail}</div>}
                                    </>
                                )}
                            </div>
                        </div>
                        <Tabs
                            value={settingTabView}
                            onChange={toggleSettingTabView}
                            orientation={!isNarrow ? 'vertical' : 'horizontal'}
                            variant="scrollable"
                            className={className + '__navTabs'}
                            textColor="primary"
                            indicatorColor="primary"
                            aria-label="Settings navigation"
                        >
                            <Tab
                                value={0}
                                icon={<IoPersonCircleOutline />}
                                iconPosition="start"
                                label="My Profile"
                                className={className + '__navTab ' + (settingTabView === 0 ? 'is-active' : '')}
                            />
                            <Tab
                                value={1}
                                icon={<IoShieldCheckmarkOutline />}
                                iconPosition="start"
                                label="Access Center"
                                className={className + '__navTab ' + (settingTabView === 1 ? 'is-active' : '')}
                            />
                            <Tab
                                value={2}
                                icon={<IoOptionsOutline />}
                                iconPosition="start"
                                label="Preferences"
                                className={className + '__navTab ' + (settingTabView === 2 ? 'is-active' : '')}
                            />
                            <Tab
                                value={4}
                                icon={<IoKeyOutline />}
                                iconPosition="start"
                                label="Change Password"
                                className={className + '__navTab ' + (settingTabView === 4 ? 'is-active' : '')}
                            />
                            <Tab
                                value={5}
                                icon={<IoTrashOutline />}
                                iconPosition="start"
                                label="Delete Account"
                                className={className + '__navTab ' + (settingTabView === 5 ? 'is-active' : '')}
                            />
                        </Tabs>
                    </aside>

                    {/* Main content */}
                    <main className={className + '__main'}>
                        <div className={className + '__card'}>
                            {settingTabView === 0 && <ProfilePage />}
                            {settingTabView === 1 && <AccessSettingsPage />}
                            {settingTabView === 2 && <PreferencesPage />}
                            {settingTabView === 4 && <ChangePasswordPage />}
                            {settingTabView === 5 && <DeleteAccountPage />}
                        </div>
                    </main>
                </div>
        </div>
    );
}
